import { create } from 'zustand'
import type { UserQuestionResponse } from '../types/user'

// 기존 타입 재활용
type Question = UserQuestionResponse['data'][0]

interface UserAnswer {
  questionId: number
  selectedAnswerIndex: number
  isCorrect: boolean
  timestamp: Date
}

interface LearningState {
  // 기본 정보
  levelId: number | null
  userId: number | null

  // 진행 상태
  currentStep: 'video' | 'quiz' | 'completed'
  videoCompleted: boolean
  currentQuestionIndex: number

  // 문제 및 답안 데이터
  questions: Question[]
  userAnswers: UserAnswer[]
  wrongQuestionIds: number[]

  // UI 상태
  showModal: boolean
  modalData: {
    isCorrect: boolean
    explanation: string
    correctAnswer: string
  } | null

  // 레벨 테스트 라운드 간 누적 집계
  levelTestAccTotal: number
  levelTestAccCorrect: number
}

interface LearningActions {
  // 초기화
  initializeLearning: (
    levelId: number,
    userId: number,
    questions: Question[]
  ) => void
  reset: () => void

  // 비디오 관련
  completeVideo: () => void

  // 퀴즈 관련
  goToNextQuestion: () => void
  goToPreviousQuestion: () => void
  setCurrentQuestionIndex: (index: number) => void
  submitAnswer: (
    questionId: number,
    answerIndex: number,
    isCorrect: boolean
  ) => void

  // 스텝 관련 (새로 추가)
  setCurrentStep: (step: 'video' | 'quiz' | 'completed') => void
  isAllQuestionsAnswered: () => boolean

  // 모달 관련
  showAnswerModal: (data: {
    isCorrect: boolean
    explanation: string
    correctAnswer: string
  }) => void
  closeModal: () => void

  // 완료 처리
  completeQuiz: () => void

  // 데이터 조회
  getWrongQuestionIds: () => number[]
  getAnswerSummary: () => { total: number; correct: number; wrong: number }
  getCurrentQuestion: () => Question | null
  isLastQuestion: () => boolean

  // 유저 정답 불러와서 새로 세팅
  setUserAnswers: (answers: UserAnswer[]) => void

  // 레벨 테스트 누적 집계
  accumulateLevelTestRound: () => void
  resetLevelTestAccumulator: () => void
  getLevelTestSummary: () => { total: number; correct: number; wrong: number }
}

// 오답 문제풀이 결과 보내줄 때: 문제 ID, 정답 맞았는지 틀렸는지
// QUESTION_ID:number, IS_CORRECT:string,
// {QUESTION_ID:123 ,IS_CORRECT:N,USER_ID:3} -> 오답
// {QUESTION_ID:103 ,IS_CORRECT:Y,USER_ID:3} -> 정답

export const useLearningStore = create<LearningState & LearningActions>(
  (set, get) => ({
    // 초기 상태
    levelId: null,
    userId: null,
    currentStep: 'video',
    videoCompleted: false,
    currentQuestionIndex: 0,
    questions: [],
    userAnswers: [],
    wrongQuestionIds: [],
    showModal: false,
    modalData: null,
    levelTestAccTotal: 0,
    levelTestAccCorrect: 0,

    // 초기화 액션
    initializeLearning: (levelId, userId, questions) => {
      set({
        levelId,
        userId,
        questions,
        currentStep: 'video',
        videoCompleted: false,
        currentQuestionIndex: 0,
        userAnswers: [],
        wrongQuestionIds: [],
        showModal: false,
        modalData: null,
      })
    },

    setCurrentStep: (step) => {
      set({ currentStep: step })
    },

    setUserAnswers: (answers) => {
      set({ userAnswers: answers })
    },

    accumulateLevelTestRound: () => {
      const { userAnswers, levelTestAccTotal, levelTestAccCorrect } = get()
      const correct = userAnswers.filter((a) => a.isCorrect).length
      set({
        levelTestAccTotal: levelTestAccTotal + userAnswers.length,
        levelTestAccCorrect: levelTestAccCorrect + correct,
      })
    },

    resetLevelTestAccumulator: () => {
      set({ levelTestAccTotal: 0, levelTestAccCorrect: 0 })
    },

    getLevelTestSummary: () => {
      const { levelTestAccTotal, levelTestAccCorrect } = get()
      return {
        total: levelTestAccTotal,
        correct: levelTestAccCorrect,
        wrong: levelTestAccTotal - levelTestAccCorrect,
      }
    },

    isAllQuestionsAnswered: () => {
      const { userAnswers, questions } = get()
      return userAnswers.length === questions.length
    },

    reset: () => {
      set({
        levelId: null,
        userId: null,
        currentStep: 'video',
        videoCompleted: false,
        currentQuestionIndex: 0,
        questions: [],
        userAnswers: [],
        wrongQuestionIds: [],
        showModal: false,
        modalData: null,
        levelTestAccTotal: 0,
        levelTestAccCorrect: 0,
      })
    },

    // 비디오 관련
    completeVideo: () => {
      set({ videoCompleted: true, currentStep: 'quiz' })
    },

    // 퀴즈 관련
    goToNextQuestion: () => {
      const { currentQuestionIndex, questions } = get()
      if (currentQuestionIndex < questions.length - 1) {
        set({ currentQuestionIndex: currentQuestionIndex + 1 })
      }
    },

    goToPreviousQuestion: () => {
      const { currentQuestionIndex } = get()
      if (currentQuestionIndex > 0) {
        set({ currentQuestionIndex: currentQuestionIndex - 1 })
      }
    },

    setCurrentQuestionIndex: (index) => {
      const { questions } = get()
      if (index >= 0 && index < questions.length) {
        set({ currentQuestionIndex: index })
      }
    },

    submitAnswer: (questionId, answerIndex, isCorrect) => {
      const { userAnswers, wrongQuestionIds } = get()

      // 이미 답한 문제인지 확인
      const existingAnswerIndex = userAnswers.findIndex(
        (answer) => answer.questionId === questionId
      )

      const newAnswer: UserAnswer = {
        questionId,
        selectedAnswerIndex: answerIndex,
        isCorrect,
        timestamp: new Date(),
      }

      let newUserAnswers: UserAnswer[]
      if (existingAnswerIndex >= 0) {
        // 기존 답안 업데이트
        newUserAnswers = [...userAnswers]
        newUserAnswers[existingAnswerIndex] = newAnswer
      } else {
        // 새 답안 추가
        newUserAnswers = [...userAnswers, newAnswer]
      }

      // 틀린 문제 ID 업데이트
      let newWrongQuestionIds = wrongQuestionIds.filter(
        (id) => id !== questionId
      )
      if (!isCorrect) {
        newWrongQuestionIds = [...newWrongQuestionIds, questionId]
      }

      set({
        userAnswers: newUserAnswers,
        wrongQuestionIds: newWrongQuestionIds,
      })
    },

    // 모달 관련
    showAnswerModal: (data) => {
      set({ showModal: true, modalData: data })
    },

    closeModal: () => {
      set({ showModal: false, modalData: null })
    },

    // 완료 처리
    completeQuiz: () => {
      set({ currentStep: 'completed' })
    },

    // 데이터 조회
    getWrongQuestionIds: () => {
      return get().wrongQuestionIds
    },

    getAnswerSummary: () => {
      const { userAnswers } = get()
      const correct = userAnswers.filter((answer) => answer.isCorrect).length
      const total = userAnswers.length
      return { total, correct, wrong: total - correct }
    },

    getCurrentQuestion: () => {
      const { questions, currentQuestionIndex } = get()
      return questions[currentQuestionIndex] || null
    },

    isLastQuestion: () => {
      const { questions, currentQuestionIndex } = get()
      return currentQuestionIndex === questions.length - 1
    },
  })
)
